import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import usePageMeta from '@/hooks/usePageMeta';

const ImpressumPage = () => {
  usePageMeta({
    title: 'Impressum | gowiTEC – Sondermaschinenbau Reichshof',
    description: 'Impressum von gowiTEC, Inhaber Hans-Jörg Gollan, Gewerbeparkstr. 8, 51580 Reichshof-Wehnrath. Angaben gemäß § 5 TMG.',
    canonical: 'https://gowitec.de/impressum',
    noindex: true,
  });

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <Navbar />

      {/* Header */}
      <section className="pt-32 pb-12 md:pt-40 md:pb-16 bg-[#f8fafc] border-b border-gray-100">
        <div className="max-w-4xl mx-auto px-6 md:px-10">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-sm text-gray-500 hover:text-[#1E3A8A] transition-colors cursor-pointer mb-6"
          >
            <span className="w-4 h-4 flex items-center justify-center">
              <i className="ri-arrow-left-line"></i>
            </span>
            Zurück zur Startseite
          </Link>
          <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-3">Impressum</h1>
          <p className="text-gray-500 text-sm">Angaben gemäß § 5 TMG</p>
        </div>
      </section>

      {/* Content */}
      <main className="flex-1 py-14 md:py-20">
        <div className="max-w-4xl mx-auto px-6 md:px-10">
          <div className="flex flex-col gap-10">

            {/* Unternehmensangaben */}
            <section>
              <h2 className="text-xl font-bold text-gray-900 mb-4 pb-2 border-b border-gray-100">Unternehmensangaben</h2>
              <address className="not-italic text-gray-700 text-sm leading-relaxed">
                <strong className="text-gray-900 font-semibold text-base">gowiTEC</strong><br />
                Inhaber: Hans-Jörg Gollan<br />
                Gewerbeparkstr. 8<br />
                51580 Reichshof-Wehnrath<br />
                Deutschland
              </address>
              <p className="text-sm text-gray-500 mt-3 leading-relaxed">
                Das Unternehmen ist nicht im Handels-, Vereins-, Partnerschafts- oder Genossenschaftsregister eingetragen.
              </p>
            </section>

            {/* Kontakt */}
            <section>
              <h2 className="text-xl font-bold text-gray-900 mb-4 pb-2 border-b border-gray-100">Kontakt</h2>
              <ul className="flex flex-col gap-2 text-sm text-gray-700">
                <li className="flex items-center gap-2">
                  <span className="w-4 h-4 flex items-center justify-center text-[#1E3A8A]">
                    <i className="ri-phone-line"></i>
                  </span>
                  Telefon:{' '}
                  <a href="tel:+491776512122" className="text-[#1E3A8A] hover:underline cursor-pointer">
                    +49 1776512122
                  </a>
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-4 h-4 flex items-center justify-center text-[#1E3A8A]">
                    <i className="ri-mail-line"></i>
                  </span>
                  E-Mail:{' '}
                  <a href="mailto:anfrage@gowitec.de" className="text-[#1E3A8A] hover:underline cursor-pointer">
                    anfrage@gowitec.de
                  </a>
                </li>
                <li className="flex items-center gap-2 text-gray-500">
                  <span className="w-4 h-4 flex items-center justify-center text-gray-400">
                    <i className="ri-printer-line"></i>
                  </span>
                  Telefax: nicht vorhanden
                </li>
              </ul>
            </section>

            {/* Inhaber */}
            <section>
              <h2 className="text-xl font-bold text-gray-900 mb-4 pb-2 border-b border-gray-100">Inhaber</h2>
              <p className="text-sm text-gray-700 leading-relaxed">
                <strong className="text-gray-900">Hans-Jörg Gollan</strong>
              </p>
            </section>

            {/* Umsatzsteuer-ID */}
            <section>
              <h2 className="text-xl font-bold text-gray-900 mb-4 pb-2 border-b border-gray-100">Umsatzsteuer-Identifikationsnummer</h2>
              <p className="text-sm text-gray-700 leading-relaxed">
                Umsatzsteuer-Identifikationsnummer gemäß § 27 a Umsatzsteuergesetz:<br />
                <strong className="text-gray-900">DE 196463802</strong>
              </p>
            </section>

            {/* Berufsbezeichnung */}
            <section>
              <h2 className="text-xl font-bold text-gray-900 mb-4 pb-2 border-b border-gray-100">Berufsbezeichnung und berufsrechtliche Regelungen</h2>
              <p className="text-sm text-gray-700 leading-relaxed">
                Berufsbezeichnung: <strong className="text-gray-900">Maschinenbau / Automatisierungstechnik</strong><br />
                Zuständige Kammer: <strong className="text-gray-900">Handwerkskammer zu Köln</strong><br />
                Kammergebiet: Köln, Bonn, Leverkusen und Umgebung<br />
                Verliehen in: Deutschland
              </p>
            </section>

            {/* Verantwortlich für den Inhalt */}
            <section>
              <h2 className="text-xl font-bold text-gray-900 mb-4 pb-2 border-b border-gray-100">Verantwortlich für den Inhalt nach § 18 Abs. 2 MStV</h2>
              <address className="not-italic text-gray-700 text-sm leading-relaxed">
                <strong className="text-gray-900">Hans-Jörg Gollan</strong><br />
                Gewerbeparkstr. 8<br />
                51580 Reichshof-Wehnrath
              </address>
            </section>

            {/* Streitschlichtung */}
            <section>
              <h2 className="text-xl font-bold text-gray-900 mb-4 pb-2 border-b border-gray-100">Streitschlichtung</h2>
              <p className="text-sm text-gray-700 leading-relaxed mb-3">
                Die Europäische Kommission stellt eine Plattform zur Online-Streitbeilegung (OS) bereit:{' '}
                <a
                  href="https://ec.europa.eu/consumers/odr/"
                  target="_blank"
                  rel="noopener noreferrer nofollow"
                  className="text-[#1E3A8A] hover:underline cursor-pointer"
                >
                  https://ec.europa.eu/consumers/odr/
                </a>
              </p>
              <p className="text-sm text-gray-700 leading-relaxed">
                Wir sind nicht bereit oder verpflichtet, an Streitbeilegungsverfahren vor einer Verbraucherschlichtungsstelle teilzunehmen.
              </p>
            </section>

            {/* Haftung für Inhalte */}
            <section>
              <h2 className="text-xl font-bold text-gray-900 mb-4 pb-2 border-b border-gray-100">Haftung für Inhalte</h2>
              <p className="text-sm text-gray-700 leading-relaxed mb-3">
                Als Diensteanbieter sind wir gemäß § 7 Abs. 1 TMG für eigene Inhalte auf diesen Seiten nach den allgemeinen Gesetzen verantwortlich. Nach §§ 8 bis 10 TMG sind wir als Diensteanbieter jedoch nicht verpflichtet, übermittelte oder gespeicherte fremde Informationen zu überwachen oder nach Umständen zu forschen, die auf eine rechtswidrige Tätigkeit hinweisen.
              </p>
              <p className="text-sm text-gray-700 leading-relaxed">
                Verpflichtungen zur Entfernung oder Sperrung der Nutzung von Informationen nach den allgemeinen Gesetzen bleiben hiervon unberührt. Eine diesbezügliche Haftung ist jedoch erst ab dem Zeitpunkt der Kenntnis einer konkreten Rechtsverletzung möglich. Bei Bekanntwerden von entsprechenden Rechtsverletzungen werden wir diese Inhalte umgehend entfernen.
              </p>
            </section>

            {/* Haftung für Links */}
            <section>
              <h2 className="text-xl font-bold text-gray-900 mb-4 pb-2 border-b border-gray-100">Haftung für Links</h2>
              <p className="text-sm text-gray-700 leading-relaxed mb-3">
                Unser Angebot enthält Links zu externen Websites Dritter, auf deren Inhalte wir keinen Einfluss haben. Deshalb können wir für diese fremden Inhalte auch keine Gewähr übernehmen. Für die Inhalte der verlinkten Seiten ist stets der jeweilige Anbieter oder Betreiber der Seiten verantwortlich. Die verlinkten Seiten wurden zum Zeitpunkt der Verlinkung auf mögliche Rechtsverstöße überprüft. Rechtswidrige Inhalte waren zum Zeitpunkt der Verlinkung nicht erkennbar.
              </p>
              <p className="text-sm text-gray-700 leading-relaxed">
                Eine permanente inhaltliche Kontrolle der verlinkten Seiten ist jedoch ohne konkrete Anhaltspunkte einer Rechtsverletzung nicht zumutbar. Bei Bekanntwerden von Rechtsverletzungen werden wir derartige Links umgehend entfernen.
              </p>
            </section>

            {/* Urheberrecht */}
            <section>
              <h2 className="text-xl font-bold text-gray-900 mb-4 pb-2 border-b border-gray-100">Urheberrecht</h2>
              <p className="text-sm text-gray-700 leading-relaxed mb-3">
                Die durch die Seitenbetreiber erstellten Inhalte und Werke auf diesen Seiten unterliegen dem deutschen Urheberrecht. Die Vervielfältigung, Bearbeitung, Verbreitung und jede Art der Verwertung außerhalb der Grenzen des Urheberrechtes bedürfen der schriftlichen Zustimmung des jeweiligen Autors bzw. Erstellers. Downloads und Kopien dieser Seite sind nur für den privaten, nicht kommerziellen Gebrauch gestattet.
              </p>
              <p className="text-sm text-gray-700 leading-relaxed">
                Soweit die Inhalte auf dieser Seite nicht vom Betreiber erstellt wurden, werden die Urheberrechte Dritter beachtet. Insbesondere werden Inhalte Dritter als solche gekennzeichnet. Sollten Sie trotzdem auf eine Urheberrechtsverletzung aufmerksam werden, bitten wir um einen entsprechenden Hinweis. Bei Bekanntwerden von Rechtsverletzungen werden wir derartige Inhalte umgehend entfernen.
              </p>
            </section>

          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default ImpressumPage;
